import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { fetchMyProfile, updateMyProfile } from '../api/companions';

const defaultForm = {
  displayName: '',
  tavernTitle: '',
  motto: '',
  avatarUrl: '',
  profileTags: '',
};

const tagsToText = (tags = []) => tags.join(', ');

const textToTags = (text) =>
  text
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
    .slice(0, 4);

const isDataImage = (value) => /^data:image\/(png|jpe?g|webp|gif);base64,/i.test(value);

const isValidOptionalAvatar = (value) => {
  const text = value.trim();
  if (!text) return true;
  if (isDataImage(text)) return text.length <= 250000;

  try {
    const url = new URL(text);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
};

const compressAvatarFile = (file) =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('unsupported'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('decode'));
      image.onload = () => {
        const maxSize = 512;
        const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext('2d');
        context.fillStyle = '#fffaf0';
        context.fillRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.86);
        if (dataUrl.length > 250000) {
          reject(new Error('size'));
          return;
        }

        resolve(dataUrl);
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const ProfileEditPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const avatarInputRef = useRef(null);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [avatarError, setAvatarError] = useState('');

  const validationError = useMemo(() => {
    if (!form.displayName.trim()) return t('profile.edit.validation.nameRequired');
    if (form.displayName.trim().length > 120) return t('profile.edit.validation.nameTooLong');
    if (form.tavernTitle.trim().length > 80) return t('profile.edit.validation.titleTooLong');
    if (form.motto.trim().length > 180) return t('profile.edit.validation.mottoTooLong');
    if (!isValidOptionalAvatar(form.avatarUrl)) return t('profile.edit.validation.avatarInvalid');
    if (textToTags(form.profileTags).some((tag) => tag.length > 32)) {
      return t('profile.edit.validation.tagTooLong');
    }
    return '';
  }, [form, t]);

  useEffect(() => {
    let mounted = true;

    fetchMyProfile()
      .then((profile) => {
        if (!mounted) return;
        setForm({
          displayName: profile.displayName || t('profile.defaults.displayName'),
          tavernTitle: profile.tavernTitle || t('profile.defaults.tavernTitle'),
          motto: profile.motto || t('profile.motto'),
          avatarUrl: profile.avatarUrl || '',
          profileTags: tagsToText(profile.profileTags),
        });
        setError('');
      })
      .catch(() => {
        if (!mounted) return;
        setError(t('profile.feedback.loadError'));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [t]);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    if (field === 'avatarUrl') setAvatarError('');
  };

  const handleAvatarFileChange = async (event) => {
    const [file] = event.target.files || [];
    event.target.value = '';

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setAvatarError(t('profile.edit.validation.avatarUnsupported'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setAvatarError(t('profile.edit.validation.avatarTooLarge'));
      return;
    }

    try {
      const avatarUrl = await compressAvatarFile(file);
      setForm((current) => ({ ...current, avatarUrl }));
      setAvatarError('');
    } catch {
      setAvatarError(t('profile.edit.validation.avatarProcessFailed'));
    }
  };

  const handleRemoveAvatar = () => {
    setForm((current) => ({ ...current, avatarUrl: '' }));
    setAvatarError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validationError) return;

    try {
      setSaving(true);
      setError('');
      await updateMyProfile({
        displayName: form.displayName,
        tavernTitle: form.tavernTitle,
        motto: form.motto,
        avatarUrl: form.avatarUrl,
        profileTags: textToTags(form.profileTags),
      });
      navigate('/profile', {
        replace: true,
        state: { toast: t('profile.edit.feedback.saved') },
      });
    } catch (saveError) {
      setError(t('profile.edit.feedback.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={22} />
            <Typography color="text.secondary">{t('profile.edit.feedback.loading')}</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={3.5}>
      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
          {t('profile.edit.eyebrow')}
        </Typography>
        <Typography variant="h1">{t('profile.edit.title')}</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 620 }}>
          {t('profile.edit.subtitle')}
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {avatarError && <Alert severity="warning">{avatarError}</Alert>}
      {validationError && <Alert severity="info">{validationError}</Alert>}

      <Card variant="outlined">
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3.5}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="input"
                    ref={avatarInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleAvatarFileChange}
                    sx={{ display: 'none' }}
                  />
                  <Avatar
                    src={form.avatarUrl || undefined}
                    alt={form.displayName}
                    sx={{
                      width: 118,
                      height: 118,
                      border: '4px solid',
                      borderColor: 'secondary.light',
                      boxShadow: '0 18px 34px -26px rgba(6, 37, 34, 0.8)',
                    }}
                  />
                  <IconButton
                    aria-label={t('profile.edit.actions.chooseAvatar')}
                    onClick={() => avatarInputRef.current?.click()}
                    sx={{
                      position: 'absolute',
                      right: -4,
                      bottom: -4,
                      bgcolor: 'secondary.main',
                      color: 'secondary.contrastText',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'secondary.dark' },
                    }}
                  >
                    <PhotoCameraIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <Stack spacing={2}>
                    <TextField
                      required
                      label={t('profile.edit.fields.displayName')}
                      value={form.displayName}
                      onChange={handleChange('displayName')}
                      inputProps={{ maxLength: 120 }}
                    />
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t('profile.edit.helpers.avatarImage')}
                      </Typography>
                      {form.avatarUrl ? (
                        <Button
                          variant="text"
                          color="inherit"
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={handleRemoveAvatar}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {t('profile.edit.actions.removeAvatar')}
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>
                </Box>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 2,
                }}
              >
                <TextField
                  label={t('profile.edit.fields.tavernTitle')}
                  value={form.tavernTitle}
                  onChange={handleChange('tavernTitle')}
                  inputProps={{ maxLength: 80 }}
                />
                <TextField
                  label={t('profile.edit.fields.profileTags')}
                  value={form.profileTags}
                  onChange={handleChange('profileTags')}
                  placeholder={t('profile.edit.placeholders.profileTags')}
                  helperText={t('profile.edit.helpers.profileTags')}
                />
              </Box>

              <TextField
                label={t('profile.edit.fields.motto')}
                value={form.motto}
                onChange={handleChange('motto')}
                multiline
                minRows={3}
                inputProps={{ maxLength: 180 }}
                helperText={t('profile.edit.helpers.motto', {
                  count: form.motto.length,
                  max: 180,
                })}
              />

              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={1.5}
                justifyContent="flex-end"
              >
                <Button variant="text" onClick={() => navigate('/profile')}>
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving || Boolean(validationError)}
                >
                  {saving ? t('profile.edit.actions.saving') : t('profile.edit.actions.save')}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ProfileEditPage;
